import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input"; // Adjust path if needed
import Notification from "../../../components/Notification/Notification"; // Adjust path if needed

/* ===================== SERVICES ===================== */
import { apiPost } from "../../../apiService"; // Adjust path if needed

/* ===================== STYLES ===================== */
import styles from "./AdminAddProduct.module.scss";

const AdminAddProduct = () => {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [form, setForm] = useState({
    productName: "",
    productRedirectUrl: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================= */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const validate = () => {
    const errs = {};
    if (!form.productName.trim()) {
      errs.productName = "Product name is required";
    }
    if (!form.productRedirectUrl.trim()) {
      errs.productRedirectUrl = "Redirect URL is required";
    } else if (!/^https?:\/\//i.test(form.productRedirectUrl)) {
      errs.productRedirectUrl = "URL must start with http:// or https://";
    }
    if (!form.description.trim()) {
      errs.description = "Description is required";
    }
    return errs;
  };

  /* =========================
     HANDLERS
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // Clear error on typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        productName: form.productName.trim(),
        product_redirect_url: form.productRedirectUrl.trim(),
        description: form.description.trim(),
      };

      const res = await apiPost("catalog", "/apimaster/products", payload);

      if (res?.success || res?.status === "success") {
        showNotification("Product created successfully", "success");
        // Redirect back to Product Access page after success
        setTimeout(() => navigate("/admin/product-access"), 1500);
      } else {
        showNotification(res?.message || "Failed to create product", "error");
      }
    } catch (err) {
      console.error("Product creation error:", err);
      showNotification(
        "Something went wrong while creating the product",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-add-prod__page"]}>
      {notification && (
        <div className={styles["admin-add-prod__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-add-prod__header"]}>
        {/* Navigates back to the previous page in history */}
        <button
          className={styles["admin-add-prod__back-btn"]}
          onClick={() => navigate(-1)}
          type="button"
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        <div className={styles["admin-add-prod__title-group"]}>
          <h2 className={styles["admin-add-prod__title"]}>Add New Product</h2>
          <p className={styles["admin-add-prod__subtitle"]}>
            Define a new product application to be assigned to client
            organizations.
          </p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className={styles["admin-add-prod__card"]}>
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles["admin-add-prod__grid"]}>
            {/* Product Name */}
            <Input
              label={
                <>
                  <span>Product Name</span>
                  <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                </>
              }
              type="text"
              name="productName"
              placeholder="e.g. Identity Verification API"
              value={form.productName}
              onChange={handleChange}
              error={errors.productName}
              required
            />

            {/* Redirect URL */}
            <Input
              label={
                <>
                  <span>Redirect URL</span>
                  <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                </>
              }
              type="url"
              name="productRedirectUrl"
              placeholder="https://portal.yourdomain.com"
              value={form.productRedirectUrl}
              onChange={handleChange}
              error={errors.productRedirectUrl}
              required
            />

            {/* Description (Spans full width) */}
            <div className={styles["admin-add-prod__full-width"]}>
              <Input
                label={
                  <>
                    <span>Description</span>
                    <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                      *
                    </span>
                  </>
                }
                type="text" // If your Input component supports "textarea", you can use that here
                name="description"
                placeholder="Briefly describe what this product does..."
                value={form.description}
                onChange={handleChange}
                error={errors.description}
                required
              />
            </div>
          </div>

          {/* ACTIONS */}
          <div className={styles["admin-add-prod__actions"]}>
            <button
              type="button"
              className={`${styles["admin-add-prod__btn"]} ${styles["admin-add-prod__btn--secondary"]}`}
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["admin-add-prod__btn"]} ${styles["admin-add-prod__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting ? "Saving..." : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddProduct;
